/**
 * Composable for generating responsive image attributes
 * Provides srcset, sizes, alt text, and dimensions for better performance
 *
 * @param {Object} image - WordPress image object from GraphQL
 * @returns {Object} Image attributes for binding to <img> tag
 */
export const useResponsiveImage = (image) => {
  if (!image) {
    return {
      src: '',
      alt: '',
      loading: 'lazy',
      decoding: 'async'
    };
  }

  const srcset = [];

  // Use WordPress's actual generated image sizes from mediaDetails
  if (image.mediaDetails?.sizes && Array.isArray(image.mediaDetails.sizes)) {
    // Filter out gform-specific sizes and sort by width
    const availableSizes = image.mediaDetails.sizes
      .filter(size => !size.name?.startsWith('gform-'))
      .sort((a, b) => a.width - b.width);

    // Build srcset from available sizes
    availableSizes.forEach(size => {
      if (size.sourceUrl && size.width) {
        srcset.push(`${size.sourceUrl} ${size.width}w`);
      }
    });
  }

  // Always include the original image as fallback
  if (image.sourceUrl && image.mediaDetails?.width) {
    srcset.push(`${image.sourceUrl} ${image.mediaDetails.width}w`);
  }

  return {
    src: image.sourceUrl || '',
    srcset: srcset.length > 0 ? srcset.join(', ') : undefined,
    // Responsive sizes:
    // - Mobile: 100vw (full width)
    // - Tablet: 50vw (half width)
    // - Desktop: 33vw (third width)
    sizes: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
    alt: image.altText || image.title || '',
    width: image.mediaDetails?.width,
    height: image.mediaDetails?.height,
    loading: 'lazy',
    decoding: 'async'
  };
};

/**
 * Generate srcset for background images converted to <img>
 * Similar to useResponsiveImage but with object-cover classes
 *
 * @param {Object} image - WordPress image object
 * @returns {Object} Image attributes + CSS classes
 */
export const useBackgroundImage = (image) => {
  const attrs = useResponsiveImage(image);

  return {
    ...attrs,
    class: 'absolute inset-0 w-full h-full object-cover -z-10',
    alt: '', // Background images should have empty alt
  };
};

/**
 * Get optimized image URL for CSS background-image
 * Returns the most appropriate WordPress-generated size based on expected usage
 *
 * @param {Object} image - WordPress image object from GraphQL
 * @param {String} preferredSize - Preferred size name (thumbnail, medium, large, full)
 * @returns {String} Optimized image URL
 */
export const getBackgroundImageUrl = (image, preferredSize = 'large') => {
  if (!image) return '';

  // Return getImageSrc for compatibility
  return getImageSrc(image, preferredSize);
};
